import type { LinkProps } from 'next/link';
import Link from 'next/link';
import type { AnchorHTMLAttributes } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';

import {
  clearNavigatingPath,
  getNavigatingPath,
  setNavigatingPath,
} from '@/utils/navigationControl';
import {
  buildResolvedPath,
  ensureLocale,
  getLinkAriaLabel,
  isPathMatch,
} from '@/utils/urlUtils';

import { useLocale } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

type LinkWithLocaleProps = LinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & {
    external?: boolean;
  };

const LinkWithLocale: React.FC<LinkWithLocaleProps> = ({
  'aria-label': providedAriaLabel,
  children,
  href,
  onClick,
  rel,
  target,
  ...rest
}) => {
  const routerLocale = useLocale();
  const { locale: regionLocale } = useRegionProvider();
  const languageCode = regionLocale.code;

  const localizedHref = useMemo(
    () => ensureLocale(href, routerLocale),
    [href, routerLocale],
  );

  // get aria-label
  const ariaLabel = useMemo(() => {
    if (providedAriaLabel) {
      return providedAriaLabel;
    }

    if (typeof href === 'string') {
      const dictionaryLabel = getLinkAriaLabel(href, languageCode);
      if (dictionaryLabel) {
        return dictionaryLabel;
      }
    } else if (href && typeof href === 'object' && href.pathname) {
      const dictionaryLabel = getLinkAriaLabel(href.pathname, languageCode);
      if (dictionaryLabel) {
        return dictionaryLabel;
      }
    }

    // No aria-label found - return undefined and let Link component handle it
    return undefined;
  }, [providedAriaLabel, href, languageCode]);

  const normalizedRel = useMemo(() => {
    if (target !== '_blank') {
      return rel;
    }

    const tokens =
      rel
        ?.split(/\s+/)
        .map((token) => token.trim())
        .filter((token) => token.length > 0) ?? [];

    const lowerCaseTokens = tokens.map((token) => token.toLowerCase());
    const extraTokens = tokens.filter((_, index) => {
      const lowerCase = lowerCaseTokens[index];
      return lowerCase !== 'noopener' && lowerCase !== 'noreferrer';
    });

    return ['noopener', 'noreferrer', ...extraTokens].join(' ');
  }, [rel, target]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const targetHref =
        typeof localizedHref === 'string'
          ? localizedHref
          : (buildResolvedPath(
              localizedHref.pathname ?? '',
              localizedHref.query,
            ) ?? '');

      if (!href || targetHref === '') {
        onClick?.(e);
        return;
      }

      if (isPathMatch(getNavigatingPath(), targetHref)) {
        e.preventDefault();
        return;
      }

      setNavigatingPath(targetHref);
      onClick?.(e);
    },
    [localizedHref, onClick, href],
  );

  useEffect(() => {
    const handlePopstate = () => {
      const currentPath = window.location.pathname + window.location.search;
      if (isPathMatch(getNavigatingPath(), currentPath)) {
        clearNavigatingPath();
      }
    };

    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  return (
    <Link
      aria-disabled={!!getNavigatingPath()}
      aria-label={ariaLabel}
      data-redirecting={!!getNavigatingPath()}
      href={localizedHref}
      onClick={handleClick}
      rel={normalizedRel || undefined}
      target={target}
      {...rest}
    >
      {children}
    </Link>
  );
};

export default memo(LinkWithLocale);
